import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Typography, Form, Select, Flex, Button, FormInstance, Card, Badge, Tooltip } from "antd";
import moment from "moment";
import MasterComponent from "../../../components/Forms";
import { FormColumnConfig, MasterConfigs } from "../../../interfaces/entity";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import { constants } from "../../../constants/common";
import DrawerComponent from "../../../components/Forms/Drawer";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import ContactDetails from "./DealsDetails";
import DealsDetails from "./DealsDetails";
import { getData } from "../../../actions/apiCall";
import RenderFormItem from "../../../components/Forms/FormItems";
import { createActivitiesColumn } from "../../../utils/activitiesColumn";
const { Text, Title } = Typography;

const Deals = () => {
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [selectedDeal, setSelectedDeal] = React.useState(null);
    const [selectedID, setSelectedID] = React.useState(null);
    const [form, setForm] = React.useState<FormInstance | null>(null);
    const [leadType, setLeadType] = React.useState(undefined);
    const [selectedAccount, setSelectedAccount] = React.useState(undefined);
    const [contractType, setContractType] = React.useState(undefined);
    const [invoiceSettings, setInvoiceSettings] = React.useState<any>(null);
    const [projectType, setProjectType] = React.useState<string | undefined>(undefined);
    const [stagesData, setStagesData] = React.useState<{ [leadTypeId: string]: any[] }>({});
    const [stagesCache, setStagesCache] = React.useState<{ [key: string]: any[] }>({});

    const dispatch = useDispatch();

    // States for upcoming activities
    const [upcomingActivities, setUpcomingActivities] = useState<{ [key: string]: { date: string | Date; hasUpcoming: boolean } }>({});
    const { refreshTable, user } = useSelector((app: any) => app?.app || {});
    const [tableData, setTableData] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const isAdmin = user?.designation === 'Admin';

    // Extract upcoming activities info from deals API response
    const extractUpcomingActivitiesFromRecords = useCallback((records: any[]) => {
        if (!records || records.length === 0) return;

        const activitiesMap: { [key: string]: { date: string | Date | null; hasUpcoming: boolean } } = {};

        records.forEach((record) => {
            if (!record?._id) return;
            const recordKey = `deals-${record._id}`;

            // Use upcomingActivity from API response if available
            if (record.upcomingActivity) {
                activitiesMap[recordKey] = {
                    date: record.upcomingActivity.upcomingDate || null,
                    hasUpcoming: record.upcomingActivity.hasUpcoming || false
                };
            } else {
                // Default to no upcoming activities if not provided
                activitiesMap[recordKey] = {
                    date: null,
                    hasUpcoming: false
                };
            }
        });

        setUpcomingActivities(prev => ({
            ...prev,
            ...activitiesMap
        }));
    }, []);

    // Refresh upcoming activities when table refreshes
    useEffect(() => {
        if (refreshTable && tableData.length > 0) {
            extractUpcomingActivitiesFromRecords(tableData);
        }
    }, [refreshTable, tableData.length, extractUpcomingActivitiesFromRecords]);

    // Fetch invoice settings to determine contract type
    useEffect(() => {
        const fetchInvoiceSettings = async () => {
            try {
                const resData = await dispatch(getData('/invoices/settings'));
                if (resData?.code === 200 && resData?.data) {
                    setInvoiceSettings(resData.data);
                }
            } catch (error) {
                console.error('Error fetching invoice settings:', error);
            }
        };
        fetchInvoiceSettings();
    }, [dispatch]);

    // Check if current leadType is contract type
    useEffect(() => {
        if (leadType && invoiceSettings?.contractType) {
            const isContract = leadType === invoiceSettings.contractType;
            setContractType(isContract ? 'C2C' : null);

            // Auto-set projectType to StaffAugmentation for contracts (only if not already set to FixBid)
            if (isContract && form) {
                const currentProjectType = form.getFieldValue('projectType');
                // Only set to StaffAugmentation if not already FixBid
                if (currentProjectType !== 'FixBid') {
                    form.setFieldValue('projectType', 'StaffAugmentation');
                    setProjectType('StaffAugmentation');
                }
            }
        } else {
            setContractType(null);
        }
    }, [leadType, invoiceSettings, form]);

    // Auto-select first stage is handled in the stage field's onDataLoad callback
    // This uses the data already loaded by the combo, avoiding duplicate API calls

    const handleProbalityChange = async (id) => {
        const resData = await dispatch(getData('/stages' + `/${id}`));
        if (resData?.data && form) {
            const getformData = form.getFieldsValue();
            form.setFieldValue('probability', resData?.data?.probability);
            if (getformData?.amount)
                form.setFieldValue('expectedRevenue', (getformData.amount * resData?.data?.probability) / 100);
        }
    };

    // Auto-generate deal name based on contract type
    const generateDealName = async (formValues: any) => {
        if (!form) return;

        const accountId = formValues?.business;
        const developerId = formValues?.developer;
        const projectName = formValues?.projectName; // Changed from project to projectName (text field)
        const closingDate = formValues?.closingDate;
        const projectType = formValues?.projectType;

        // Only auto-generate if it's a contract (C2C) or FixBid
        const isContract = contractType === 'C2C';
        const isFixBid = projectType === 'FixBid';

        if (!isContract && !isFixBid) return;

        try {
            let accountName = '';
            let developerName = '';
            let dateStr = '';

            // Get account name
            if (accountId) {
                const accountRes = await dispatch(getData(`/accounts/${accountId}`));
                if (accountRes?.code === 200 && accountRes?.data) {
                    accountName = accountRes.data.businessName || '';
                }
            }

            // Get developer name for C2C
            if (isContract && developerId) {
                const developerRes = await dispatch(getData(`/bench-developers/${developerId}`));
                if (developerRes?.code === 200 && developerRes?.data) {
                    const dev = developerRes.data;
                    developerName = [dev.firstName, dev.lastName].filter(Boolean).join(' ') || '';
                }
            }

            // Format date
            if (closingDate) {
                dateStr = moment(closingDate).format('DD-MM-YYYY');
            } else {
                dateStr = moment().format('DD-MM-YYYY');
            }

            // Generate deal name
            let dealName = '';
            if (isContract && accountName && developerName) {
                dealName = `C2C - ${accountName} - ${developerName} - ${dateStr}`;
            } else if (isFixBid && accountName && projectName) {
                dealName = `FB - ${accountName} - ${projectName} - ${dateStr}`;
            }

            if (dealName && form) {
                form.setFieldValue('dealName', dealName);
            }
        } catch (error) {
            console.error('Error generating deal name:', error);
        }
    };

    const config: MasterConfigs = useMemo(() => ({
        api: () => '/deals',
        title: 'Deals',
        setForm: (formInstance) => setForm(formInstance),
        queryParams: {
            andOr: 'and', // Use 'and' to combine filters
        },
        setInitialForm: () => {
            // For normal users, set default dealOwner to current user when creating new deal
            if (!isAdmin && user?._id) {
                return { dealOwner: user._id };
            }
            return {};
        },
        handleValuesChange: async (changedValues: any, allValues: any) => {
            // Auto-generate deal name when relevant fields change
            if (changedValues.closingDate || changedValues.business || changedValues.developer || changedValues.projectName || changedValues.projectType || changedValues.leadType) {
                await generateDealName(allValues);
            }

            // Auto-select first stage when leadType changes (using combo's loaded data)
            if (changedValues.leadType && form) {
                const newLeadType = changedValues.leadType;
                setLeadType(newLeadType);

                try {
                    // Get lead type name to check if it's FixBid
                    let leadTypeName = '';
                    if (allValues.leadType) {
                        // Try to get from the combo's loaded data if available
                        // Otherwise, we'll fetch it
                        const leadTypeRes = await dispatch(getData(`/leads-types?current=1&pageSize=100`));
                        if (leadTypeRes?.code === 200 && leadTypeRes?.data && Array.isArray(leadTypeRes.data)) {
                            const foundLeadType = leadTypeRes.data.find((lt: any) => lt._id === newLeadType);
                            if (foundLeadType) {
                                leadTypeName = foundLeadType.name || '';

                                // Check if Lead Type is FixBid and set Project Type accordingly
                                // Handle both "fixbid" and "fix bid" (with or without space)
                                const normalizedLeadTypeName = leadTypeName.toLowerCase().replace(/\s+/g, '');
                                if (normalizedLeadTypeName.includes('fixbid')) {
                                    form.setFieldValue('projectType', 'FixBid');
                                    setProjectType('FixBid');
                                }
                            }
                        }
                    }

                    // Auto-select first stage using combo's loaded data (stored in stagesData state)
                    // Wait a bit for the combo to load stages, then check stored data
                    const checkAndSelectFirstStage = () => {
                        const currentStage = form.getFieldValue('currentStage');
                        if (!currentStage && stagesData[newLeadType] && stagesData[newLeadType].length > 0) {
                            // Sort stages by order and select the first one
                            const sortedStages = [...stagesData[newLeadType]].sort((a, b) => {
                                const orderA = parseInt(a.order) || 0;
                                const orderB = parseInt(b.order) || 0;
                                return orderA - orderB;
                            });
                            const firstStage = sortedStages[0];
                            if (firstStage?._id) {
                                form.setFieldValue('currentStage', firstStage._id);
                                handleProbalityChange(firstStage._id);
                            }
                        } else if (!currentStage) {
                            // If stages not loaded yet, check again after a delay
                            setTimeout(checkAndSelectFirstStage, 300);
                        }
                    };
                    setTimeout(checkAndSelectFirstStage, 300);
                } catch (error) {
                    console.error('Error handling lead type change:', error);
                }
            }
        },
        activitiesColumn: {
            enabled: true,
            modelName: 'deals',
            title: 'Activities',
            width: 120,
            fixed: 'left',
            align: 'center',
            color: 'purple'
        },
        onDataLoad: (data: any) => {
            // Store table data and extract upcoming activities info from API response
            const records = Array.isArray(data) ? data : (data?.data || []);
            if (records && Array.isArray(records) && records.length > 0) {
                setTableData(records);
                extractUpcomingActivitiesFromRecords(records);
            }
        },
        onSummaryLoad: (summaryData: any) => {
            console.log('Deals summary data received:', summaryData);
            setSummary(summaryData);
        },
        renderSummary: () => {
            if (!summary) return null;

            // Format total budget with currency
            const formatCurrency = (amount: number) => {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0
                }).format(amount || 0);
            };

            return (
                <div style={{ width: '100%', marginBottom: 16 }}>
                    <Flex gap={12} wrap="wrap" style={{ width: '100%' }}>
                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #1890ff', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', flexWrap: 'wrap', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Deal Status:
                                </Text>
                                <Flex gap={12} align="center" wrap="wrap">
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.inProgress || 0}
                                            title="In Progress"
                                            style={{ backgroundColor: '#faad14' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#faad14', lineHeight: '30px', margin: 0 }}>P</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.loss || 0}
                                            title="Loss"
                                            style={{ backgroundColor: '#ff4d4f' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#ff4d4f', lineHeight: '30px', margin: 0 }}>L</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.won || 0}
                                            title="Total Won"
                                            style={{ backgroundColor: '#52c41a' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#52c41a', lineHeight: '30px', margin: 0 }}>W</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.total || 0}
                                            title="Total Deals"
                                            style={{ backgroundColor: '#1890ff' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#1890ff', lineHeight: '30px', margin: 0 }}>D</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Card>

                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #722ed1', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', flexWrap: 'wrap', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Date Range:
                                </Text>
                                <Flex gap={12} align="center" wrap="wrap">
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.today || 0}
                                            title="Today"
                                            style={{ backgroundColor: '#722ed1' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#722ed1', lineHeight: '30px', margin: 0 }}>T</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.lastWeek || 0}
                                            title="Last Week"
                                            style={{ backgroundColor: '#eb2f96' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#eb2f96', lineHeight: '30px', margin: 0 }}>W</Text>
                                    </Flex>
                                    <Flex align="center" gap={4} style={{ whiteSpace: 'nowrap', height: '30px' }}>
                                        <Badge
                                            count={summary.lastMonth || 0}
                                            title="Last Month"
                                            style={{ backgroundColor: '#2f54eb' }}
                                            styles={{ indicator: { width: '30px', height: '30px', minWidth: '30px', lineHeight: '30px', fontSize: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
                                        />
                                        <Text style={{ fontSize: '12px', fontWeight: 600, color: '#2f54eb', lineHeight: '30px', margin: 0 }}>M</Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ backgroundColor: '#fff', borderLeft: '4px solid #13c2c2', height: '100%' }}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between', minHeight: '32px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0, lineHeight: '20px' }}>
                                    Total Budget:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600, lineHeight: '20px' }}>
                                    {formatCurrency(summary.totalBudget || 0)}
                                </Title>
                            </Flex>
                        </Card>
                    </Flex>
                </div>
            );
        },
        modalTitle: (text) => text + ' Deal',
        isActionTab: true,
        getFilterColumns: () => {
            const filterColumns: any[] = [
                {
                    label: 'Lead Type',
                    name: 'leadType',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Lead Type',
                        showSearch: true,
                    },
                },
                {
                    label: 'Stage',
                    name: 'currentStage',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Stage',
                        showSearch: true,
                    },
                },
            ];

            // Deal Owner filter - only visible to Admin
            if (isAdmin) {
                filterColumns.push({
                    label: 'Deal Owner',
                    name: 'dealOwner',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Deal Owner',
                        showSearch: true,
                    },
                });
            }

            filterColumns.push(
                {
                    label: 'Account Name',
                    name: 'business',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Account',
                        showSearch: true,
                    },
                },
                {
                    label: 'Contact Name',
                    name: 'contact',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Contact',
                        showSearch: true,
                    },
                },
                {
                    label: 'Deal Name',
                    name: 'dealName',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by deal name',
                },
                {
                    label: 'Lead Source',
                    name: 'leadSource',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Lead Source',
                        showSearch: true,
                    },
                    children: constants.LEAD_SOURCES.map(source => ({
                        name: source.name,
                        value: source.value
                    }))
                },
                {
                    label: 'Type',
                    name: 'type',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Type',
                    },
                    children: [
                        { name: 'None', value: 'None' },
                        { name: 'New Business', value: 'New' },
                        { name: 'Existing Business', value: 'Existing' },
                    ]
                },
                {
                    label: 'Billing Type',
                    name: 'billingType',
                    type: 'select',
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Billing Type',
                    },
                    children: [
                        { name: 'Monthly', value: 'Monthly' },
                        { name: 'Hourly', value: 'Hourly' },
                    ]
                },
                // Date range filters for created date
                {
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select start date',
                },
                {
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select end date',
                }
            );

            return filterColumns;
        },
        getColumns: () => {
            const columns: any[] = [];

            // Add Activities column if enabled in config
            const activitiesColumn = createActivitiesColumn(config.activitiesColumn);
            if (activitiesColumn) {
                columns.push(activitiesColumn);
            }

            // Add other columns
            columns.push(
                {
                    title: 'Deal Name',
                    dataIndex: 'dealName',
                    key: 'dealName',
                    width: 150,
                    render: (text, record) => <a onClick={() => handleLeadClick(record)}>{text}</a>,
                    ellipsis: true,
                },
                {
                    width: 150,
                    title: 'Amount',
                    dataIndex: 'amount',
                    key: 'amount',
                },
                {
                    width: 150,
                    title: 'Stage',
                    dataIndex: 'currentStage',
                    key: 'currentStage',
                    render: (text) => text.name,
                },
                {
                    width: 150,
                    title: 'Closing Date',
                    dataIndex: 'closingDate',
                    key: 'closingDate',
                    render: (text) => moment(text).format('DD MMM YYYY')
                },
                {
                    title: 'Account Name',
                    dataIndex: 'business',
                    key: 'business',
                    width: 150,
                    render: (text) => text?.businessName || 'N/A',
                    ellipsis: true,
                },
                {
                    title: 'Contact Name',
                    dataIndex: 'contact',
                    key: 'contact',
                    width: 200,
                    fixed: 'left',
                    render: (text, record) => <div >
                        <SmallProfileCard
                            name={[text?.preName, text?.firstName, text?.lastName].filter(Boolean).join(' ')}
                            avatar={text?.profilePhoto ? text?.profilePhoto : undefined}
                            subTitle={text?.title}
                            initials={text?.firstName?.charAt(0)}
                        />
                    </div>
                },
                {
                    title: 'Deal Owner',
                    dataIndex: 'dealOwner',
                    key: 'dealOwner',
                    render: (text) => <SmallProfileCard
                        name={constants.GET_FULL_NAME(text)}
                        avatar={text?.avatar ? text?.avatar : undefined}
                        subTitle={text?.employeeID || text?.workEmail}
                    />,
                    width: 150
                },
                {
                    title: 'Created At',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (text) => moment(text).format('DD MMM YYYY HH:mm'),
                    align: 'center',
                    width: 150,
                }
            );

            return columns;
        },
        getFormColumns: () => [
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Deal Information</Title>
            },
            {
                label: 'Deal Owner',
                name: 'dealOwner',
                type: 'select',
                isCombo: true,
                span: 12,
            },
            {
                label: 'Billing Type',
                name: 'billingType',
                type: 'select',
                span: 12,
                children: [
                    { name: 'Monthly', value: 'Monthly' },
                    { name: 'Hourly', value: 'Hourly' },
                ],
            },
            {
                label: 'Amount',
                name: 'amount',
                type: 'text',
                dependencies: ['billingType'],
                inputProps: {
                    prefix: 'INR ',
                    onChange: (e) => {
                        const value: any = e.target.value;
                        const amountRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                        if (amountRegex.test(value) && form) {
                            form.setFieldValue('expectedRevenue', (value * form.getFieldValue('probability')) / 100);
                        }
                    }
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const amountRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return amountRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid amount'));
                        },
                    },
                ],
                span: 12,
            },
            {
                label: (
                    <Flex align="center" gap={4}>
                        <span>Deal Name</span>
                        <Tooltip
                            title={
                                <div>
                                    <div style={{ marginBottom: 4 }}>Deal name is auto-generated based on:</div>
                                    <div>• <strong>C2C Contracts:</strong> C2C - {`{Account name}`} - {`{Developer name}`} - {`{date}`}</div>
                                    <div>• <strong>FixBid Deals:</strong> FB - {`{Account name}`} - {`{Project name}`} - {`{date}`}</div>
                                </div>
                            }
                            placement="top"
                        >
                            <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
                        </Tooltip>
                    </Flex>
                ),
                name: 'dealName',
                type: 'text',
                rules: [{ required: true, message: 'Deal Name is required' }],
                span: 12,
                placeholder: 'Auto-generated for contracts and FixBid deals',
                inputProps: {
                    placeholder: 'Auto-generated for contracts and FixBid deals'
                }
            },
            {
                label: 'Lead Type',
                name: 'leadType',
                type: 'select',
                isCombo: true,
                selectProps: {
                    onChange: async (value, option: any) => {
                        setLeadType(value);
                        if (form && value) {
                            const formValues = form.getFieldsValue();
                            formValues.leadType = value;

                            try {
                                // Get lead type name from option data (already loaded by combo)
                                let leadTypeName = '';

                                // Try to get name from option data (combo stores original data in option.data)
                                if (option?.data?.name) {
                                    leadTypeName = option.data.name;
                                } else if (option?.label && typeof option.label === 'string') {
                                    // Fallback to label if data not available
                                    leadTypeName = option.label;
                                }

                                // Check if Lead Type is FixBid and set Project Type accordingly
                                if (leadTypeName && leadTypeName.toLowerCase().includes('fixbid')) {
                                    form.setFieldValue('projectType', 'FixBid');
                                    setProjectType('FixBid');
                                }

                                // Note: First stage auto-selection is handled by useEffect when leadType changes
                                // This avoids duplicate API calls since the combo will also load the stages
                            } catch (error) {
                                console.error('Error fetching lead type or stages:', error);
                            }

                            await generateDealName(formValues);
                        }
                    },
                    onSelect: (value, option: any) => {
                        // onSelect receives the option with data, use it if onChange doesn't have it
                        // This is a backup in case onChange doesn't receive option data
                    }
                },
                rules: [{ required: true, message: 'Lead Type is required' }],
                span: 12,
            },
            // Developer field for C2C contracts
            contractType === 'C2C' ? {
                label: 'Developer',
                name: 'developer',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Developer is required for contracts' }],
                span: 12,
                selectProps: {
                    onChange: async (value) => {
                        if (form) {
                            const formValues = form.getFieldsValue();
                            formValues.developer = value;
                            await generateDealName(formValues);
                        }
                    }
                }
            } : null,
            // Project Type and Project fields for FixBid
            {
                label: 'Project Type',
                name: 'projectType',
                type: 'select',
                span: 12,
                children: [
                    { name: 'None', value: 'None' },
                    { name: 'FixBid', value: 'FixBid' },
                    { name: 'Staff Augmentation', value: 'StaffAugmentation' },
                ],
                selectProps: {
                    disabled: contractType === 'C2C', // Disable for contracts as it's auto-set
                    onChange: async (value) => {
                        setProjectType(value);
                        if (form) {
                            const formValues = form.getFieldsValue();
                            formValues.projectType = value;
                            // Clear project name if not FixBid
                            if (value !== 'FixBid') {
                                form.setFieldValue('projectName', undefined);
                            }
                            await generateDealName(formValues);
                        }
                    }
                }
            },
            projectType === 'FixBid' ? {
                label: 'Project Name',
                name: 'projectName',
                type: 'text',
                rules: [{ required: true, message: 'Project Name is required for FixBid deals' }],
                span: 12,
                inputProps: {
                    placeholder: 'Enter project name',
                    onChange: async (e) => {
                        if (form) {
                            const formValues = form.getFieldsValue();
                            formValues.projectName = e.target.value;
                            await generateDealName(formValues);
                        }
                    }
                }
            } : null,
            {
                label: 'Closing Date',
                name: 'closingDate',
                type: 'date',
                span: 12,
                rules: [{ required: true, message: 'Closing Date is required' }],
            },
            {
                label: 'Account Name',
                name: 'business',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Account Name is required' }],
                span: 12,
                selectProps: {
                    onChange: async (value) => {
                        setSelectedAccount(value);
                        if (form) {
                            // Clear contact when account changes
                            form.setFieldValue('contact', undefined);
                            // Trigger deal name generation
                            const formValues = form.getFieldsValue();
                            formValues.business = value;
                            await generateDealName(formValues);
                        }
                    }
                }
            },
            {
                label: 'Stage',
                name: 'currentStage',
                isCombo: true,
                type: 'select',
                rules: [{ required: true, message: 'Stage is required' }],
                span: 12,
                selectProps: {
                    showSearch: true,
                    disabled: !leadType,
                    optionFilterProp: 'children',
                    allowClear: true,
                    onChange: (value) => {
                        if (form && value) {
                            handleProbalityChange(value);
                        }
                    },
                    onDataLoad: (stages: any[]) => {
                        // Store stages data when combo loads it (for use in handleValuesChange)
                        if (leadType && stages && stages.length > 0) {
                            setStagesData(prev => ({ ...prev, [leadType]: stages }));
                        }
                    },
                },
            },
            {
                label: 'Contact Name',
                name: 'contact',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Contact Name is required' }],
                span: 12,
                selectProps: {
                    disabled: !selectedAccount,
                    placeholder: selectedAccount ? 'Select contact' : 'Select account first'
                }
            },
            {
                label: 'Type',
                name: 'type',
                type: 'select',
                span: 12,
                children: [
                    { name: 'None', value: 'None' },
                    { name: 'New Business', value: 'New' },
                    { name: 'Existing Business', value: 'Existing' },
                ],
            },
            {
                label: 'Probability (%)',
                name: 'probability',
                type: 'number',
                span: 12
            },
            {
                label: 'Next Step',
                name: 'nextStep',
                type: 'text',
                span: 12,
            },
            {
                label: 'Expected Revenue',
                name: 'expectedRevenue',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                    disabled: true,
                },
                span: 12,
            },
            {
                label: 'Lead Source',
                name: 'leadSource',
                type: 'select',
                span: 12,
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.LEAD_SOURCES
            },
            {
                type: 'custom',
                span: 24,
                formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 24,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 },
                }
            },
        ].filter(Boolean) as FormColumnConfig[],
        combo: [
            // Deal Owner combo - always include, but for normal users, default to current user
            {
                api: '/users',
                name: 'dealOwner',
                idKey: '_id',
                render: (text) => <ProfileCard
                    size={30}
                    name={constants.GET_FULL_NAME(text)}
                    avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                    role={null}
                />
            },
            {
                api: leadType ? `/stages?category=Deals&leadType=${leadType}&andOr=and` : `/stages?category=Deals`,
                name: 'currentStage',
                idKey: '_id',
                render: (text) => text.name,
                rerender: [leadType]
            },
            {
                api: '/accounts',
                name: 'business',
                idKey: '_id',
                render: (text) => text.businessName
            },
            {
                api: selectedAccount ? `/contacts?business=${selectedAccount}&andOr=and` : '/contacts',
                name: 'contact',
                idKey: '_id',
                render: (text) => constants.GET_FULL_NAME(text),
                rerender: [selectedAccount]
            },
            // Developer combo for contracts
            ...(contractType === 'C2C' ? [{
                api: '/bench-developers?vendorPartner=All',
                name: 'developer',
                idKey: '_id',
                render: (text) => [text.firstName, text.lastName].filter(Boolean).join(' ') || 'N/A'
            }] : []),
            {
                api: '/leads-types',
                name: 'leadType',
                idKey: '_id',
                render: (text) => text.name,
                // Pass the full option object to onChange handler
                optionLabelProp: 'name'
            }
        ],
        actions: [
            {
                actionType: 'filter',
                name: 'Filter',
            },
            {
                name: 'Add Deal',
                actionType: 'Add',
            }
        ]
    }), [form, leadType, isAdmin, summary, extractUpcomingActivitiesFromRecords, selectedAccount, contractType, invoiceSettings, projectType]);

    const handleLeadClick = (record) => {
        setSelectedDeal(record);
        setOpenDrawer(true);
    }

    const handleEditClick = (record) => {
        setSelectedID(record?._id);
        setOpenDrawer(true);
    }

    return (
        <>
            <MasterComponent config={config} isModal={true} recordId={selectedID} />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={selectedDeal?.dealName}
                            avatarUrl={null}
                            role={selectedDeal?.business?.name}
                        />
                        {!selectedDeal?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => handleEditClick(selectedDeal)} />
                        </div>}
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%', style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <DealsDetails
                    deal={selectedDeal}
                    onDealUpdate={(updatedDeal) => {
                        setSelectedDeal(updatedDeal);
                    }}
                />
            </DrawerComponent>
        </>
    );
}

export default Deals;